import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, Button, LinearProgress, Typography, useMediaQuery } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

const PAGE_SIZE = 15;

// Runs a column's own renderer so the phone list shows exactly what the grid
// cell would - avatars, status marks, action buttons - without a second copy.
const renderFor = (col, row) => {
  const value = row[col.field];
  const params = { id: row.id, field: col.field, row, value, formattedValue: value, colDef: col };
  if (col.valueFormatter) {
    params.formattedValue = col.valueFormatter(params);
  }
  return col.renderCell ? col.renderCell(params) : params.formattedValue;
};

const isBlank = (value) => value === null || value === undefined || value === '';

const PhoneList = ({ rows, columns, getRowId, onRowClick, loading, slots, mobile = {} }) => {
  const [visible, setVisible] = useState(PAGE_SIZE);
  const idOf = (row) => (getRowId ? getRowId(row) : row.id);

  const shown = columns.filter((col) => !(mobile.hide || []).includes(col.field));
  const actionsCol = shown.find((col) => col.field === 'actions');
  const titleCol =
    shown.find((col) => col.field === mobile.title) || shown.find((col) => col.field !== 'actions');
  const amountCol = shown.find((col) => col.field === (mobile.amount || 'amount') && col !== titleCol);
  const detailCols = shown.filter((col) => col !== titleCol && col !== amountCol && col !== actionsCol);
  const Empty = slots?.noRowsOverlay;

  if (rows.length === 0) {
    return (
      <Box>
        {loading && <LinearProgress />}
        {!loading && (Empty ? <Empty /> : <Typography sx={{ p: 3 }} color="text.secondary">No rows</Typography>)}
      </Box>
    );
  }

  return (
    <Box>
      {loading && <LinearProgress />}
      {rows.slice(0, visible).map((row, index) => (
        <Box
          key={idOf(row)}
          onClick={onRowClick ? () => onRowClick({ id: idOf(row), row }) : undefined}
          sx={{
            p: 1.5,
            borderTop: index === 0 ? 'none' : '1px solid',
            borderColor: 'divider',
            cursor: onRowClick ? 'pointer' : 'default',
            '&:active': onRowClick ? { bgcolor: 'action.hover' } : undefined,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>{titleCol && renderFor(titleCol, row)}</Box>
            {amountCol && (
              <Box sx={{ flexShrink: 0, textAlign: 'right', typography: 'body2' }}>{renderFor(amountCol, row)}</Box>
            )}
            {actionsCol && <Box sx={{ flexShrink: 0, display: 'flex' }}>{renderFor(actionsCol, row)}</Box>}
          </Box>

          {detailCols.length > 0 && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' },
                columnGap: 2,
                rowGap: 1,
                mt: 1.25,
              }}
            >
              {detailCols
                .filter((col) => col.renderCell || !isBlank(row[col.field]))
                .map((col) => (
                  <Box key={col.field} sx={{ minWidth: 0 }}>
                    <Typography variant="overline" sx={{ display: 'block', lineHeight: 1.6 }}>
                      {col.headerName}
                    </Typography>
                    <Box sx={{ typography: 'body2', minWidth: 0, overflowWrap: 'anywhere' }}>{renderFor(col, row)}</Box>
                  </Box>
                ))}
            </Box>
          )}
        </Box>
      ))}

      {rows.length > visible && (
        <Button fullWidth onClick={() => setVisible((count) => count + PAGE_SIZE)} sx={{ py: 1.5, borderRadius: 0 }}>
          Show more ({rows.length - visible} left)
        </Button>
      )}
    </Box>
  );
};

// Drop-in for <DataGrid>: identical on desktop, a stacked list below `md`
// where 700px+ of columns would otherwise force sideways scrolling.
// `mobile` picks the row's title/amount and hides columns on the phone list.
const ResponsiveDataGrid = ({ mobile, ...gridProps }) => {
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));

  if (!isCompact) {
    return <DataGrid {...gridProps} />;
  }

  return (
    <PhoneList
      rows={gridProps.rows || []}
      columns={gridProps.columns || []}
      getRowId={gridProps.getRowId}
      onRowClick={gridProps.onRowClick}
      loading={gridProps.loading}
      slots={gridProps.slots}
      mobile={mobile}
    />
  );
};

export default ResponsiveDataGrid;
